// frontend/src/components/editor/hooks/useBlockOperations.ts
import { useState, useCallback, useRef } from 'react';
import { ContentBlock } from '../../../types';
import { toast } from '../../../store/toastStore';
import { imagePlacementApi } from '../../../services/imagePlacementApi';
import api from '../../../services/api';

interface UseBlockOperationsOptions {
  projectId: number;
  blocks: ContentBlock[];
  setBlocks: (blocks: ContentBlock[]) => void;
  modelConfigId: number | null;
  useRAG?: boolean;
  selectedSkillId?: number | null;
  onImagePickerOpen?: (blockId: string, queryText: string) => void;
}

const ACTION_TO_TEXT: Record<string, string> = {
  regenerate: '重新生成',
  polish: '润色',
  expand: '扩写',
  condense: '缩写',
};

export function useBlockOperations({
  projectId,
  blocks,
  setBlocks,
  modelConfigId,
  useRAG = false,
  selectedSkillId = null,
  onImagePickerOpen,
}: UseBlockOperationsOptions) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [streamingBlockId, setStreamingBlockId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const updateBlock = useCallback(
    (blockId: string, updater: (block: ContentBlock) => ContentBlock) => {
      const updated = blocks.map((b) => (b.id === blockId ? updater(b) : b));
      setBlocks(updated);
    },
    [blocks, setBlocks]
  );

  const handleBlockAction = useCallback(
    async (blockId: string, action: string) => {
      const block = blocks.find((b) => b.id === blockId);
      if (!block) return;

      // 图片操作
      if (block.type === 'image') {
        if (action === 'replace' || action === 'ai-generate') {
          // 如果 block 有 slotId，直接用 ImageReplaceModal
          const queryText = block.meta?.queryText || '';
          const slotId = block.meta?.slotId || '';

          if (action === 'ai-generate' && slotId) {
            // 直接生成并替换
            const API_BASE = process.env.REACT_APP_API_URL ?? '';
            const token = localStorage.getItem('token');
            try {
              setIsRegenerating(true);
              setStreamingBlockId(blockId);
              const res = await fetch(`${API_BASE}/api/projects/${projectId}/blocks/image/generate`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ block_id: blockId, query_text: queryText }),
              });
              const data = await res.json();
              if (data.ok && data.image) {
                await imagePlacementApi.updateBySlot(projectId, slotId, data.image.id);
                updateBlock(blockId, (b) => ({
                  ...b,
                  content: `${process.env.REACT_APP_API_URL ?? ''}/${data.image.file_path.replace(/^\//, '')}`,
                  meta: { ...b.meta, queryText: queryText, slotId },
                }));
                toast.success('图片已更新');
              } else if (!data.ok) {
                toast.error(data.error || '图片生成失败，可尝试简化描述或更换图表类型');
              }
            } catch (e: any) {
              toast.error(`图片操作失败: ${e.message}`);
            } finally {
              setIsRegenerating(false);
              setStreamingBlockId(null);
            }
            return;
          }

          if (action === 'replace') {
            onImagePickerOpen?.(blockId, queryText);
            return;
          }
        }
        if (action === 'delete') {
          setBlocks(blocks.filter((b) => b.id !== blockId));
          toast.success('图片已删除');
          return;
        }
      }

      // 表格操作 — 添加行/列
      if (block.type === 'table' && (action === 'add-row' || action === 'add-col')) {
        toast.info(`${action === 'add-row' ? '添加行' : '添加列'}功能待实现`);
        return;
      }

      // 文本类操作（regenerate/polish/expand/condense） → SSE 流式生成
      if (!modelConfigId) {
        toast.error('请先在「模型设置」中配置 AI 模型');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setIsRegenerating(true);
      setStreamingBlockId(blockId);

      // 构建上下文（前后各一个块）
      const idx = blocks.findIndex((b) => b.id === blockId);
      let context = '';
      if (idx > 0) {
        const prev = blocks[idx - 1];
        context += `[上文] ${prev.content.replace(/<[^>]*>/g, '').slice(0, 300)}\n`;
      }
      if (idx < blocks.length - 1) {
        const next = blocks[idx + 1];
        context += `[下文] ${next.content.replace(/<[^>]*>/g, '').slice(0, 300)}`;
      }

      try {
        const resp = await fetch(`${API_BASE}/api/projects/${projectId}/blocks/regenerate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            chapter_uid: '',
            block_id: blockId,
            block_type: block.type,
            action,
            current_content: block.content,
            context: context || undefined,
            model_config_id: modelConfigId,
            use_web_search: useRAG,
            ...(selectedSkillId ? { skill_id: selectedSkillId } : {}),
          }),
          signal: abortRef.current.signal,
        });

        if (!resp.ok) throw new Error(await resp.text());

        const reader = resp.body?.getReader();
        if (!reader) throw new Error('无法读取响应流');

        const decoder = new TextDecoder();
        let newContent = '';
        let buf = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            const t = line.trim();
            if (!t || !t.startsWith('data: ')) continue;
            const json = t.slice(6);
            if (json === '[DONE]') continue;
            try {
              const p = JSON.parse(json);
              if (p.type === 'block_chunk' && p.block_id === blockId) {
                newContent += p.chunk || '';
                updateBlock(blockId, (b) => ({
                  ...b,
                  content: newContent,
                }));
              } else if (p.type === 'block_end' && p.block_id === blockId) {
                // 生成完成
              } else if (p.type === 'error') {
                throw new Error(p.message);
              }
            } catch (e: any) {
              if (e.message && !e.message.includes('Unexpected token')) throw e;
            }
          }
        }

        // 根据 action 构造合适的 HTML 包装
        let wrapped = newContent;
        if (block.type === 'paragraph' && !wrapped.startsWith('<p')) {
          wrapped = `<p>${wrapped.replace(/\n/g, '<br>')}</p>`;
        }

        updateBlock(blockId, (b) => ({ ...b, content: wrapped }));
        toast.success(`${ACTION_TO_TEXT[action] || '操作'}完成`);
      } catch (e: any) {
        if (e.name === 'AbortError') return;
        toast.error(`${ACTION_TO_TEXT[action] || '操作'}失败: ${e.message}`);
      } finally {
        setIsRegenerating(false);
        setStreamingBlockId(null);
      }
    },
    [
      blocks,
      setBlocks,
      updateBlock,
      projectId,
      modelConfigId,
      useRAG,
      selectedSkillId,
      onImagePickerOpen,
    ]
  );

  const cancelOperation = useCallback(() => {
    abortRef.current?.abort();
    setIsRegenerating(false);
    setStreamingBlockId(null);
  }, []);

  return {
    handleBlockAction,
    isRegenerating,
    streamingBlockId,
    cancelOperation,
  };
}
